/*
 *   File : orders.js
 *   Author : https://evoqins.com
 *   Description : orders component (API integrated)
 *   Integrations : orders/filter-data, orders/list, orders/list-download (optional)
 *   Version : 1.1.0
 */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { PrimaryButton } from "../../Components/Buttons";
import { Filter, CustomDatePicker } from "../../Components/Filters";
import { DataTableComponent } from "../../Components/DataTable";
import { ScrollInfo } from "../../Components/Other";
import { useDispatch, useSelector } from "react-redux";
import {
  setPageTitle,
  setSearchEnabled,
  setPlaceHolder,
  clearSearchQuery,
} from "../../Redux/Ui/ui.slice";
import { useNavigate } from "react-router-dom";
import { Tabs } from "../../Components/Navigation";
import { Chip, EmptyScreen, Loader } from "../../Components/Other";
import { toast } from "react-toastify";
import { APIService } from "../../Service";
import {
  clearListState,
  saveListState,
  LIST_STATE_KEY,
} from "../../Redux/Filter/filterSlice";

const TAB_DATA = [
  {
    id: 0,
    label: "all-orders",
    name: "All Orders",
  },
  {
    id: 2,
    label: "mutual-funds",
    name: "Mutual Funds",
  },
  {
    id: 1,
    label: "goals",
    name: "Goals",
  },
];

// Column id -> sort_by values from orders/filter-data ([ascending, descending] arrow)
const COLUMN_SORT_MAP = {
  order_date: [2, 1],
  order_id: [3, 4],
  investment_type: [5, 6],
  transaction_type: [7, 8],
  amount: [10, 9],
  payment_status: [13, 14],
  order_status: [11, 12],
};

// Stable fallback so the list effect doesn't refetch on every render
const NO_FILTERS = {};

export default function Orders() {
  const navigate = useNavigate();

  const [loader, setLoader] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [sortOptions, setSortOptions] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [selectedRow, setSelectedRow] = useState({ selectedRows: [] });
  const [isAllSelected, setIsAllSelected] = useState(false);

  const listFetchIdRef = useRef(0);

  //Redux title and searchbar setting
  const dispatch = useDispatch();

  // Tab, page & page size live in the Redux store so returning from order detail restores them
  const listState = useSelector(
    (state) => state.persistedFilters[LIST_STATE_KEY]?.orders,
  );
  const curTab = listState?.tab ?? 0;
  const page = listState?.page ?? 1;
  const perPage = listState?.perPage ?? 10;
  // Filter modal selections and date range are kept per tab, so each tab filters independently
  const filtersByTab = listState?.filtersByTab;
  const appliedFilters = filtersByTab?.[curTab] ?? NO_FILTERS;
  const dateRangeByTab = listState?.dateRangeByTab;
  // { from_date, till_date } in DD-MM-YYYY, or empty for all time
  const dateRange = dateRangeByTab?.[curTab];
  const fromDate = dateRange?.from_date || null;
  const tillDate = dateRange?.till_date || null;
  // { columnId, asc } of the active column sort arrow, or null for the API default
  const sort = listState?.sort ?? null;
  const sortBy = sort ? COLUMN_SORT_MAP[sort.columnId]?.[sort.asc ? 0 : 1] : null;

  const _updateListParams = (next) => {
    dispatch(saveListState("orders", next));
  };
  const setPage = (p) => _updateListParams({ page: p });

  // Investment type only applies to "All Orders"; the other tabs are already one type
  const isAllOrdersTab = curTab === 0;
  const tabFilterData = useMemo(
    () =>
      isAllOrdersTab
        ? filterData
        : filterData.filter((f) => f.title !== "investment_type"),
    [filterData, isAllOrdersTab],
  );
  const investmentType = isAllOrdersTab
    ? appliedFilters.investment_type
    : undefined;

  useEffect(() => {
    dispatch(setPageTitle(["Orders"]));
    dispatch(setSearchEnabled(true));
    dispatch(setPlaceHolder("Search with User name or Order ID"));
    return () => {
      listFetchIdRef.current += 1;
      dispatch(clearSearchQuery());
    };
  }, [dispatch]);

  // searchQuery from global UI (same as users.js)
  const searchQuery = useSelector((state) => state.ui.searchQuery);

  useEffect(() => {
    _getFilterData();
  }, []);

  // A new search must start from page 1, otherwise results beyond page 1 look empty
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    _getOrders();
  }, [
    appliedFilters,
    curTab,
    searchQuery,
    page,
    perPage,
    fromDate,
    tillDate,
    sortBy,
  ]);

  // Select All covers every page of the current list, so only a different list clears it
  useEffect(() => {
    setIsAllSelected(false);
  }, [appliedFilters, curTab, searchQuery, perPage, fromDate, tillDate, sortBy]);

  // Clear persisted filters & list state when leaving Orders (except to order detail).
  // Deferred so StrictMode's simulated unmount/remount doesn't wipe them.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      setTimeout(() => {
        if (isMountedRef.current) return;
        const goingToDetail =
          window.location.pathname.startsWith("/orders/detail");
        if (!goingToDetail) {
          dispatch(clearListState("orders"));
        }
      });
    };
  }, []);

  // A column gets a sort arrow only when filter-data returned both of its sort_by values
  const _isSortable = (columnId) =>
    Boolean(COLUMN_SORT_MAP[columnId]) &&
    COLUMN_SORT_MAP[columnId].every((value) =>
      sortOptions.some((opt) => opt.value === value),
    );

  const COLUMNS = [
    {
      id: "order_date",
      name: "Date",
      selector: (row) => row.order_date,
      sortable: _isSortable("order_date"),
      width: "160px",
    },

    {
      name: "User Name",
      selector: (row) => row.name,
      wrap: true,
      width: "220px",
      cell: (row) => (
        <div
          className="d-flex column-gap-2 align-items-center e-cursor-pointer e-table-link-group"
          role="link"
          tabIndex={0}
          onClick={() => _openDetail(row)}
          onKeyDown={(e) => {
            if (e.key === "Enter") _openDetail(row);
          }}
        >
          {row.profile_image ? (
            <img
              src={row.profile_image}
              alt={row.name}
              className="flex-shrink-0 rounded-circle object-fit-cover"
              style={{
                width: "20px",
                height: "20px",
                minWidth: "20px",
                aspectRatio: "1 / 1",
              }}
            />
          ) : (
            <div
              className="d-flex flex-shrink-0 align-items-center justify-content-center rounded-circle bg-secondary text-white fw-semibold"
              style={{ width: "20px", height: "20px", fontSize: "9px" }}
            >
              {row.name?.trim()?.slice(0, 2)?.toUpperCase() || "NA"}
            </div>
          )}
          <span className="text-facebook-blue e-table-link e-table-user-name">
            {row.name || "N/A"}
          </span>
        </div>
      ),
    },

    {
      id: "order_id",
      name: "Order ID",
      selector: (row) => row.order_display_id,
      sortable: _isSortable("order_id"),
      width: "200px",
      cell: (row) => (
        <span
          className="e-cursor-pointer fs-14 fw-400 lh-20 text-facebook-blue e-table-link"
          role="link"
          tabIndex={0}
          onClick={() => _openDetail(row)}
          onKeyDown={(e) => {
            if (e.key === "Enter") _openDetail(row);
          }}
        >
          {row.order_display_id}
        </span>
      ),
    },

    {
      id: "investment_type",
      name: "Investment Type",
      sortable: _isSortable("investment_type"),
      selector: (row) => row.investment_type,
      width: "170px",
    },

    {
      id: "transaction_type",
      name: "Transaction type",
      selector: (row) => row.type,
      width: "170px",
      sortable: _isSortable("transaction_type"),
      cell: (row) => (
        <Chip
          text={row.type || "N/A"}
          style="px-2 py-1 bg-lavender-blue bg-opacity-43"
          textClr="text-facebook-blue"
        />
      ),
    },

    {
      id: "amount",
      name: "Amount",
      sortable: _isSortable("amount"),
      selector: (row) => row.amount_text,
      width: "140px",
    },

    {
      id: "payment_status",
      name: "Payment Status",
      sortable: _isSortable("payment_status"),
      selector: (row) => row.payment_status,
      width: "170px",
      cell: (row) => (
        <p
          className={`${
            row.payment_status === "Success"
              ? "text-medium-sea-green"
              : "text-copper-red"
          } mb-0`}
        >
          {row.payment_status}
        </p>
      ),
    },

    {
      id: "order_status",
      name: "Order Status",
      sortable: _isSortable("order_status"),
      selector: (row) => row.order_status,
      width: "160px",
      cell: (row) => (
        <p
          className={`${
            row.order_status === "Success"
              ? "text-medium-sea-green"
              : "text-copper-red"
          } mb-0`}
        >
          {row.order_status}
        </p>
      ),
    },

    {
      name: "Action",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue e-table-link"
          name="View order"
          onPress={() => _openDetail(row)}
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const _openDetail = (row) => {
    navigate(
      `/orders/detail/${row.investment_type?.toLowerCase() || "order"}/${row.order_id}`,
    );
  };

  //service to get filterData; sort_by drives the column sort arrows, not the Filter modal
  const _getFilterData = () => {
    APIService(false, "orders/filter-data")
      .then((res) => {
        if (res?.status_code !== 200 || !Array.isArray(res.data)) return;
        const list = res.data;
        setSortOptions(list.find((e) => e.title === "sort_by")?.options || []);
        setFilterData(list.filter((e) => e.title !== "sort_by"));
      })
      .catch((err) => {
        console.error("Failed to load orders filter data:", err);
      });
  };

  //service to get orders list (server side)
  const _getOrders = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);
    // Table remounts after loading, which clears its internal selection;
    // Select All survives page changes and re-selects the new page on remount
    setSelectedRow({ selectedRows: [] });
    // Filter/search/date/tab changes already reset page to 1, so always request the current page
    const payload = {
      tab_type: curTab,
      order_status: appliedFilters.order_status || [],
      payment_status: appliedFilters.payment_status || [],
      query: searchQuery || "",
      type: appliedFilters.transaction_type || [],
      sort_by: sortBy ?? null,
      investment_type: investmentType || [],
      from_date: fromDate,
      to_date: tillDate,
      page_num: page,
      page_size: perPage,
    };

    APIService(true, "orders/list", payload)
      .then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res.status_code === 200) {
          const orders = res.data?.orders || [];
          setTableData(orders);
          setTotalRows(res.data?.total_count || orders.length);
          setIsEmpty(orders.length === 0);
        } else {
          setTableData([]);
          setTotalRows(0);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        if (fetchId !== listFetchIdRef.current) return;
        console.error("Orders list error:", err);
        setTableData([]);
        setTotalRows(0);
        setIsEmpty(true);
      })
      .finally(() => {
        if (fetchId === listFetchIdRef.current) {
          setLoader(false);
        }
      });
  };

  // API - Download Orders List
  const _downloadOrdersList = () => {
    if (isEmpty) return;
    const rows = selectedRow?.selectedRows ?? [];
    if (rows.length === 0) return;
    setDownloadLoading(true);
    const order_ids = isAllSelected ? [] : rows.map((elem) => elem.order_id);
    const params = {
      tab_type: curTab,
      order_status: appliedFilters.order_status || [],
      payment_status: appliedFilters.payment_status || [],
      query: searchQuery || "",
      type: appliedFilters.transaction_type || [],
      investment_type: investmentType || [],
      // order-download expects YYYY-MM-DD, while the date picker gives DD-MM-YYYY
      start_date: fromDate ? fromDate.split("-").reverse().join("-") : null,
      end_date: tillDate ? tillDate.split("-").reverse().join("-") : null,
      order_ids,
    };
    // order-download expects null instead of [] for empty lists
    const payload = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        Array.isArray(value) && value.length === 0 ? null : value,
      ]),
    );

    const is_post = true;
    const url = "orders/order-download";

    APIService(is_post, url, payload)
      .then((response) => {
        if (response.status_code === 200 && response.data?.url) {
          const fileUrl = response.data.url;

          // Create temporary anchor element
          const link = document.createElement("a");
          link.href = fileUrl;
          link.setAttribute("download", ""); // Let browser handle filename
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          toast.success(response.message || "File downloaded successfully", {
            className: "e-toast",
          });
        } else {
          toast.error(response.message || "Failed to download report", {
            className: "e-toast",
          });
        }
      })
      .catch((error) => {
        console.error("Error downloading orders list:", error);
        toast.error("Failed to download report", {
          className: "e-toast",
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  function _onFilterApply(selectedValues) {
    _updateListParams({
      filtersByTab: { ...filtersByTab, [curTab]: selectedValues },
      page: 1,
    });
  }

  // Column sort arrow -> sort_by for orders/list; keeps the current page (sorting doesn't change the row count).
  // Same column cycles asc -> desc -> off (sort_by: null); a new column starts at asc.
  // The table's own direction is ignored: it only toggles and carries it over across columns.
  const _onSort = (column, direction) => {
    if (!COLUMN_SORT_MAP[column?.id]) return;
    // Under StrictMode the table re-fires onSort with its restored default sort on remount.
    // A real click always flips the direction, so a call matching the saved sort is that echo.
    if (sort?.columnId === column.id && sort.asc === (direction === "asc")) {
      return;
    }
    let nextSort = null;
    if (sort?.columnId !== column.id) {
      nextSort = { columnId: column.id, asc: true };
    } else if (sort.asc) {
      nextSort = { columnId: column.id, asc: false };
    }
    _updateListParams({ sort: nextSort });
  };

  const handleSelectedRowsChange = (state) => {
    setSelectedRow(state);
    setIsAllSelected(state.allSelected);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column row-gap-3 pt-3">
          <div className="row">
            <div className="col-lg-3 col-md-4 col-sm-5 offset-lg-9 offset-md-8 offset-sm-7">
              <div className="d-flex justify-content-end ps-3">
                <PrimaryButton
                  ClassName="e-download-btn rounded-16  bg-midnight-blue lh-24 py-2 w-70 text-white fs-16 fw-400"
                  name={downloadLoading ? "Downloading..." : "Download Report"}
                  loading={downloadLoading}
                  disabled={
                    downloadLoading ||
                    loader ||
                    isEmpty ||
                    (selectedRow?.selectedRows?.length ?? 0) === 0
                  }
                  onPress={_downloadOrdersList}
                />
              </div>
            </div>
          </div>
          <div className="e-glass-card rounded-16 d-flex flex-column rg-12 bg-transparent">
            <div className="row px-24 pt-24">
              <div className="col-lg-6">
                <Tabs
                  data={TAB_DATA}
                  defaultActive={curTab}
                  onChange={(activeId) =>
                    _updateListParams({ tab: activeId, page: 1 })
                  }
                />
              </div>
              <div className="col-lg-6">
                <div className="d-flex justify-content-end align-items-stretch column-gap-3">
                  <CustomDatePicker
                    value={dateRange}
                    placeholder="All Time"
                    fillHeight
                    onChange={({ from_date, till_date } = {}) =>
                      _updateListParams({
                        dateRangeByTab: {
                          ...dateRangeByTab,
                          [curTab]:
                            from_date && till_date
                              ? { from_date, till_date }
                              : null,
                        },
                        page: 1,
                      })
                    }
                  />
                  {/* Remount per tab so the modal shows that tab's own selections */}
                  <Filter
                    key={curTab}
                    filters={tabFilterData}
                    initialSelections={appliedFilters}
                    onApply={_onFilterApply}
                  />
                </div>
                <div className="d-flex justify-content-end pt-2">
                  <ScrollInfo />
                </div>
              </div>
            </div>

            {/* outlet */}
            <div className="row">
              <div className="col-12">
                {loader ? (
                  <Loader />
                ) : isEmpty ? (
                  <EmptyScreen message={"No Orders Found"} />
                ) : (
                  <DataTableComponent
                    columns={COLUMNS}
                    data={tableData}
                    pagination={true}
                    paginationServer={true}
                    selectableRows={true}
                    allSelected={isAllSelected}
                    currentPage={page}
                    sortServer={true}
                    onSort={_onSort}
                    defaultSortFieldId={sort?.columnId}
                    defaultSortAsc={sort?.asc ?? true}
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={(p) => setPage(p)} // receives next page
                    onChangeRowsPerPage={(newPerPage) =>
                      _updateListParams({ page: 1, perPage: newPerPage })
                    }
                    onSelectedRowsChange={handleSelectedRowsChange}
                    className="e-table-basic"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
